import Auth from "j-toker";
import {autobind} from "core-decorators";
import {action, computed, observable, reaction} from "mobx";
import _difference from "lodash/difference";

import {PieceQuery, SessionQuery} from "@/graphql/types";
import {importUser} from "@/lib/backend";
import {type PlanName} from "@/shared/plans";
import Apollo from "./Apollo";
import SessionQueryNode from "./SessionQuery.gql";
import StageStore from "./StageStore";

// Example:
// allow_password_change: false
// configName: "default"
// email: "admin@voiceful.io"
// id: 1
// image: null
// name: "Admin"
// nickname: null
// provider: "email"
// signedIn: true
// uid: "admin@voiceful.io"
export interface VoicefulUser {
  allow_password_change: boolean;
  configName: string;
  email: string;
  id: number;
  image: string | null;
  name: string;
  nickname: string | null;
  provider: string;
  signedIn: boolean;
  uid: string;
}

const LOCAL_STORAGE_ENSEMBLE = "selectedEnsemble";

// Return a standard Promise from a jQuery.Promise
function _promisify<T>(deferred: JQuery.Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => deferred.then(resolve).fail(reject));
}

@autobind
export default class Session {
  @observable initialized = false;
  @observable sessionData?: SessionQuery;
  @observable selectedEnsemble?: SessionQuery["ensembles"][0];
  @observable voicefulUser?: VoicefulUser;
  apollo: Apollo;
  stageStore: StageStore;

  // Get the authentication headers stored by j-toker from local storage if they exist.
  static get authHeaders() {
    return JSON.parse(localStorage.getItem("authHeaders") || "{}");
  }

  constructor(apollo: Apollo, stageStore: StageStore) {
    this.apollo = apollo;
    this.stageStore = stageStore;
    // Change selectedEnsemble if active ensembles change
    reaction(
      () => this.activeEnsembles,
      (activeEnsembles) => {
        if (!activeEnsembles) this.setSelectedEnsemble(undefined);
        else {
          let activeEnsemble: SessionQuery["ensembles"][0] | undefined;
          // Try to keep using the ensemble from the localStorage
          const ensembleIdStr = localStorage.getItem(LOCAL_STORAGE_ENSEMBLE);
          if (ensembleIdStr) {
            const ensembleId = parseInt(ensembleIdStr);
            activeEnsemble = activeEnsembles.find((ensemble) => ensemble.id === ensembleId);
          }
          // Pick the first ensemble as failover option
          if (!activeEnsemble) activeEnsemble = activeEnsembles[0];
          this.setSelectedEnsemble(activeEnsemble);
        }
      },
    );
    // Save selectedEnsemble to localStorage
    reaction(
      () => this.selectedEnsemble,
      (selectedEnsemble) => {
        if (selectedEnsemble === undefined) localStorage.removeItem(LOCAL_STORAGE_ENSEMBLE);
        else localStorage.setItem(LOCAL_STORAGE_ENSEMBLE, selectedEnsemble.id.toString());
      },
    );
    this.constructorAsync();
    // Change auth url when stage changes
    reaction(
      () => this.stageStore.stage,
      (stage) => {
        if (this.isSignedIn) throw new Error("Cannot change stage when signed in");
        // the second argument is necessary to overwrite the configuration, it destroys all session
        // data (but we must already be signed out anyway)
        Auth.configure(this.authConfiguration(), true);
      },
    );
  }

  async constructorAsync() {
    // Check that user is logged in
    try {
      const user = await _promisify<VoicefulUser>(Auth.configure(this.authConfiguration()));
      this.setVoicefulUser(user);
    } catch (error) {
      console.log("Auth failed:", error.reason);
    }

    // Load session data from backend
    if (this.voicefulUser) await this.fetchSessionData();

    // Mark store as initialized
    this.initialize();
  }

  // Return those ensembles that are active
  @computed({keepAlive: true})
  get activeEnsembles() {
    return this.ensembles.filter((ensemble) => {
      // owner has to be subscribed
      if (ensemble.owner.active_subscriptions.length < 1) return false;
      const ownerSubscription = ensemble.owner.active_subscriptions[0];
      // If the owner has Ensemble plan, that sufficient
      if (this.hasPlan("Ensemble", ownerSubscription.plan_id)) return true;
      // If the owner has Pro plan, I need to have any subscription active
      if (this.hasPlan("Pro", ownerSubscription.plan_id)) return this.activeSubscription !== undefined;
      // If the owner has Basic plan, the ensemble is only active for the owner
      if (this.hasPlan("Basic", ownerSubscription.plan_id)) return ensemble.owner_id === this?.user?.id;
      return false;
    });
  }

  // Return the active subscription of the signed in user in case it has any
  @computed({keepAlive: true})
  get activeSubscription() {
    return this.user?.active_subscriptions?.[0];
  }

  // Return the configuration for j-toker auth
  // https://github.com/lynndylanhurley/j-toker#configuration
  authConfiguration() {
    return {
      apiUrl: this.stageStore.stage.authApiUrl,
      storage: "localStorage",
      // confirmationSuccessUrl: () => paths.fullUrl(paths.root()),
      // passwordResetSuccessUrl: () => paths.fullUrl(paths.updatePassword()),
      authProviderPaths: {
        google: "/auth/google_oauth2",
      },
    };
  }

  get canAddPiece(): boolean {
    return this.isConductorOfProOrEnsemble;
  }

  @computed({keepAlive: true})
  get isConductorOfProOrEnsemble(): boolean {
    return this.isEnsembleConductor && (this.ownerHasPlanPro || this.ownerHasPlanEnsemble);
  }

  @computed({keepAlive: true})
  get ensembleExtraSingers(): number | null {
    return this.activeSubscription?.extra_singers ?? null;
  }

  @computed({keepAlive: true})
  get ensembles(): SessionQuery["ensembles"] {
    return this.sessionData?.ensembles || [];
  }

  // Return ensembles owned by the signed in user
  @computed({keepAlive: true})
  get ensemblesOwned(): SessionQuery["ensembles"] {
    return this.ensembles.filter((ensemble) => ensemble.owner_id === this.voicefulUser?.id);
  }

  // fetch Session data from backend
  async fetchSessionData() {
    if (!this.voicefulUser) throw new Error("[Session.fetchSessionData] voicefulUser is undefined");
    const response = await this.apollo.client.query<SessionQuery>({
      query: SessionQueryNode,
      variables: {userId: this.voicefulUser?.id},
    });
    this.setSessionData(response.data);
  }

  // Return if the signed in user has subscriptions which are not active.
  // This is useful to know if the user has previous subscriptions that have already finished
  @computed({keepAlive: true})
  get hasInactiveSubscriptions(): boolean {
    return this.user?.inactive_subscriptions?.length === 1;
  }

  // Return if the signed in user is subscribed with a plan with planName
  hasPlan(planName: PlanName, planId: string | undefined = this.activeSubscription?.plan_id): boolean {
    return planId?.includes(planName) || false;
  }

  hasRole(desiredRole: string): boolean {
    return this.selectedEnsemble?.user_ensembles?.[0]?.role === desiredRole;
  }

  // Return if the signed in user is subscribed with the basic plan
  @computed({keepAlive: true})
  get hasPlanBasic(): boolean {
    return this.hasPlan("Basic");
  }

  // Return if the signed in user is subscribed with the ensemble plan
  @computed({keepAlive: true})
  get hasPlanEnsemble(): boolean {
    return this.hasPlan("Ensemble");
  }

  // Return if the signed in user is subscribed with the pro plan
  @computed({keepAlive: true})
  get hasPlanPro(): boolean {
    return this.hasPlan("Pro");
  }

  // Return those ensembles that are not active
  @computed({keepAlive: true})
  get inactiveEnsembles() {
    return _difference(this.ensembles, this.activeEnsembles);
  }

  @action
  initialize() {
    this.initialized = true;
  }

  // @computed
  // get isAdmin(): boolean {
  //   return Boolean(this.user && User.isAdmin(this.user.email));
  // }

  // Return if the signed in user is a conductor of the selected ensemble
  @computed({keepAlive: true})
  get isEnsembleConductor(): boolean {
    return this.hasRole("conductor");
  }

  // Return if the signed in user owns the selected ensemble
  @computed({keepAlive: true})
  get isEnsembleOwner(): boolean {
    return this.isSelectedEnsembleOwner(this.voicefulUser?.id);
  }

  // Return if the signed in user is a singer of the selected ensemble
  @computed({keepAlive: true})
  get isEnsembleSinger(): boolean {
    return this.hasRole("singer");
  }

  // Return if the signed in user owns the selected ensemble
  isSelectedEnsembleOwner(userId?: number): boolean {
    if (userId === undefined) return false;
    return this.selectedEnsemble?.owner_id === userId;
  }

  @computed({keepAlive: true})
  get isSignedIn(): boolean {
    return this.voicefulUser !== undefined && this.user !== undefined && this.sessionData?.ensembles !== undefined;
  }

  // Return if the signed in user is subscribed
  @computed({keepAlive: true})
  get isSubscribed(): boolean {
    // A user is subscribed if it has an active subscription
    return this.activeSubscription !== undefined;
  }

  // Return if the owner of the selectedEnsemble is subscribed to a certain plan
  ownerHasPlan(planName: PlanName, planId?: string): boolean {
    if (planId === undefined) {
      if (!this.selectedEnsemble) return false;
      const ownerSubscription = this.selectedEnsemble.owner.active_subscriptions[0];
      if (!ownerSubscription) return false;
      planId = ownerSubscription.plan_id;
    }
    return this.hasPlan(planName, planId);
  }

  // Return if if the owner of the selectedEnsemble is subscribed with the ensemble plan
  @computed({keepAlive: true})
  get ownerHasPlanEnsemble(): boolean {
    return this.ownerHasPlan("Ensemble");
  }

  // Return if if the owner of the selectedEnsemble is subscribed with the pro plan
  @computed({keepAlive: true})
  get ownerHasPlanPro(): boolean {
    return this.ownerHasPlan("Pro");
  }

  pieceBelongsToEnsembles(piece: PieceQuery["piece"]): boolean {
    return Boolean(piece && this.ensembles.find((ensemble) => ensemble.id === piece.choir_id));
  }

  // Send password reset instructions by email
  // The email will contain a url with the current url plus a parameter "reset_password_token" and
  // the user will have been automatically logged in
  // https://github.com/lynndylanhurley/j-toker#authrequestpasswordreset
  async resetPassword(email: string) {
    return _promisify<{success: boolean; message: string}>(Auth.requestPasswordReset({email}));
  }

  @action
  setSelectedEnsemble(ensemble?: SessionQuery["ensembles"][0]) {
    this.selectedEnsemble = ensemble;
  }

  setSelectedEnsembleById(ensembleId: number) {
    const ensemble = this.ensembles.find((ensemble) => ensembleId === ensemble.id);
    if (ensemble) this.setSelectedEnsemble(ensemble);
  }

  @action
  setSelectedEnsembleName(name: string) {
    if (!this.selectedEnsemble) throw new Error("There is no selectedEnsemble");
    this.selectedEnsemble.name = name;
  }

  @action
  setSessionData(sessionData?: SessionQuery) {
    this.sessionData = sessionData;
  }

  @action
  setVoicefulUser(voicefulUser?: VoicefulUser) {
    this.voicefulUser = voicefulUser;
  }

  // https://github.com/lynndylanhurley/j-toker#authemailsignin
  async signIn({email, password}: {email: string; password: string}): Promise<VoicefulUser> {
    const response = await _promisify<{data: VoicefulUser}>(Auth.emailSignIn({email, password}));
    const user = response.data;
    this.setVoicefulUser(user);

    // If login has been successful
    if (this.voicefulUser) {
      await this.apollo.restartClient();
      // Load session data from backend
      await this.fetchSessionData();
    }

    return user;
  }

  async signInWithGoogle() {
    // This creates or updates the google user into the voiceful database and
    // sets the auth tokens in localstorage
    const user = await _promisify<VoicefulUser>(Auth.oAuthSignIn({provider: "google"}));
    if (!user) throw new Error("Error when signing with Google");

    // Now we must make sure that the created user also exists in the cantamus database
    await importUser();

    this.setVoicefulUser(user);
    // Load session data from backend
    await this.apollo.restartClient();
    await this.fetchSessionData();
  }

  // https://github.com/lynndylanhurley/j-toker#authsignout
  async signOut() {
    const response = await _promisify(Auth.signOut());
    this.setVoicefulUser(undefined);
    this.setSessionData(undefined);
    await this.apollo.restartClient();
    return response;
  }

  // Change the current user's password
  // https://github.com/lynndylanhurley/j-toker#authupdatepassword
  async updatePassword(password: string): Promise<void> {
    return _promisify(Auth.updatePassword({password, password_confirmation: password}));
  }

  @computed({keepAlive: true})
  get user(): SessionQuery["user"] | undefined {
    return this.sessionData?.user || undefined;
  }
}
